/**
 * Reusable artwork upload panel: drag & drop, local processing, immediate
 * server upload, file facts and warnings. Used by every configurator.
 */
import { useRef, useState } from 'react';
import { useI18n } from '@/i18n';
import { isApiAvailable, uploadFile } from '@/lib/api';
import { ArtworkError, disposeArtwork, processArtwork, type ProcessedArtwork } from './processArtwork';
import { useArtworkWarningTexts } from './warnings';

export type UploadStatus = 'idle' | 'uploading' | 'done' | 'failed' | 'offline';

interface Props {
  artwork: ProcessedArtwork | null;
  status: UploadStatus;
  onArtwork: (a: ProcessedArtwork | null) => void;
  onUpload: (status: UploadStatus, id?: string | null) => void;
  designHelp: boolean;
  onDesignHelp: (v: boolean) => void;
  /** Hide the "no artwork, help me" option. */
  allowDesignHelp?: boolean;
}

const ACCEPT = '.png,.jpg,.jpeg,.svg,.pdf,image/png,image/jpeg,image/svg+xml,application/pdf';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${(n / 1024 / 1024).toFixed(2)} МБ`;
}

export function ArtworkUploader({ artwork: a, status, onArtwork, onUpload, designHelp, onDesignHelp, allowDesignHelp = true }: Props) {
  const { d, nf } = useI18n();
  const t = d.cfg.upload;
  const warn = useArtworkWarningTexts();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const handle = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const art = await processArtwork(file);
      disposeArtwork(a);
      onArtwork(art);
      if (await isApiAvailable()) {
        onUpload('uploading');
        try {
          const res = await uploadFile(art.uploadBlob, file.name, 'artwork');
          onUpload('done', res.id);
        } catch {
          onUpload('failed');
        }
      } else {
        onUpload('offline');
      }
    } catch (err) {
      const code = err instanceof ArtworkError ? err.code : 'decode';
      setError(t.errors[code]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const note =
    status === 'done' ? t.uploaded : status === 'uploading' ? t.uploading : status === 'failed' ? t.uploadFailed : status === 'offline' ? t.uploadLocal : null;

  return (
    <>
      <input ref={inputRef} id="artwork-file" type="file" accept={ACCEPT} className="visually-hidden" onChange={(e) => handle(e.target.files?.[0])} />

      {!a && (
        <label
          htmlFor="artwork-file"
          className={`dropzone${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void handle(e.dataTransfer.files?.[0]);
          }}
        >
          <svg viewBox="0 0 48 48" className="dropzone__icon" aria-hidden="true">
            <path d="M24 32V10m0 0-8 8m8-8 8 8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 30v6a4 4 0 0 0 4 4h24a4 4 0 0 0 4-4v-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          <span className="dropzone__title">{busy ? t.processing : t.drop}</span>
          {!busy && (
            <span className="dropzone__sub">
              {t.or} <span className="link-like">{t.browse}</span>
            </span>
          )}
          <span className="dropzone__formats mono">{t.formats}</span>
        </label>
      )}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {a && (
        <div className="file-card">
          <div className="file-card__thumb checker">{a.thumbUrl && <img src={a.thumbUrl} alt="" />}</div>
          <div className="file-card__body">
            <p className="file-card__name">{a.fileName}</p>
            <dl className="specs">
              <div>
                <dt>{t.info.format}</dt>
                <dd>{a.format.toUpperCase()}</dd>
              </div>
              <div>
                <dt>{t.info.size}</dt>
                <dd>{formatBytes(a.sizeBytes)}</dd>
              </div>
              <div>
                <dt>{t.info.pixels}</dt>
                <dd>{a.kind === 'vector' ? t.info.vector : `${nf(a.pixelWidth!)} × ${nf(a.pixelHeight!)}`}</dd>
              </div>
              {a.pdf && (
                <div>
                  <dt>{t.info.pages}</dt>
                  <dd>
                    {a.pdf.pages} · {Math.round(a.pdf.pageWidthMm)}×{Math.round(a.pdf.pageHeightMm)} мм
                  </dd>
                </div>
              )}
              <div>
                <dt>{t.info.transparency}</dt>
                <dd>{a.hasTransparency ? t.info.yes : t.info.no}</dd>
              </div>
              {a.sha256 && (
                <div>
                  <dt>{t.info.checksum}</dt>
                  <dd className="mono" title={a.sha256}>
                    {a.sha256.slice(0, 12)}…
                  </dd>
                </div>
              )}
            </dl>
            {note && <p className={`file-card__status status--${status}`}>{note}</p>}
            <div className="btn-row">
              <label htmlFor="artwork-file" className="btn btn--ghost btn--sm">
                {busy ? t.processing : t.replace}
              </label>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  disposeArtwork(a);
                  onArtwork(null);
                }}
              >
                {t.remove}
              </button>
            </div>
          </div>
        </div>
      )}

      {a && a.warnings.length > 0 && (
        <ul className="notices">
          {warn(a.warnings).map((w) => (
            <li key={w} className="notice notice--warn">
              {w}
            </li>
          ))}
        </ul>
      )}

      {!a && allowDesignHelp && (
        <label className="check">
          <input type="checkbox" checked={designHelp} onChange={(e) => onDesignHelp(e.target.checked)} />
          <span>{t.designHelp}</span>
        </label>
      )}
      {!a && allowDesignHelp && designHelp && <p className="notice notice--info">{t.designHelpNote}</p>}
    </>
  );
}
